import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Building2, 
  Target, 
  Award, 
  Users, 
  CheckCircle2,
  ArrowRight,
  Loader2,
  Handshake,
  TrendingUp,
  Heart,
  Shield,
  Clock,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getSettings } from '@/lib/api';

const AboutPage = () => {
  const [aboutSettings, setAboutSettings] = useState(null);
  const [heroSettings, setHeroSettings] = useState(null);
  const [aboutHeroSettings, setAboutHeroSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [aboutRes, heroRes, aboutHeroRes] = await Promise.all([
          getSettings('about'),
          getSettings('hero'),
          getSettings('about_hero')
        ]);
        setAboutSettings(aboutRes.data?.value || {});
        setHeroSettings(heroRes.data?.value || {});
        setAboutHeroSettings(aboutHeroRes.data?.value || {});
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Fix old brand name if present in database
  const fixBrandName = (text) => {
    if (!text) return text;
    return text.replace(/Eta Global Danışmanlık/g, 'Eta Global Group');
  };

  const aboutRaw = aboutSettings || {
    title: 'Eta Global Group',
    subtitle: 'Güvenilir İş Ortağınız',
    description: 'Eta Global Group, personel istihdamı alanındaki deneyimi ile işletmelerin insan kaynağı süreçlerini kolaylaştıran profesyonel çözümler sunmaktadır.',
    mission: 'Doğru işe, doğru insan kaynağını yerleştirmek',
    image: 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/w8mo2k90_ChatGPT%20Image%2017%20Mar%202026%2016_03_26.png',
    values: [
      { title: 'Güvenilirlik', description: 'Her zaman sözümüzün arkasındayız' },
      { title: 'Profesyonellik', description: 'Uzman kadromuzla hizmetinizdeyiz' },
      { title: 'Müşteri Odaklılık', description: 'Sizin başarınız bizim başarımızdır' }
    ],
    whyUs: [
      'Geniş aday havuzu ve hızlı personel tedariki',
      'Sektörel deneyim ve uzmanlık',
      'Profesyonel değerlendirme süreçleri',
      'Müşteri odaklı çözüm yaklaşımı',
      '7/24 destek ve iletişim',
      'Esnek ve ekonomik fiyatlandırma'
    ]
  };
  
  const about = {
    ...aboutRaw,
    title: fixBrandName(aboutRaw.title),
    description: fixBrandName(aboutRaw.description),
    subtitle: fixBrandName(aboutRaw.subtitle),
    mission: fixBrandName(aboutRaw.mission)
  };

  const hero = heroSettings || { stats: [{ value: '10+' }, { value: '500+' }, { value: '4' }] };
  
  const aboutHero = aboutHeroSettings || {};

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section - Full Width with Image Background */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden" data-testid="about-hero">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={about.image || 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/w8mo2k90_ChatGPT%20Image%2017%20Mar%202026%2016_03_26.png'}
            alt="ETA Ekibi"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#005CB2]/95 via-[#005CB2]/80 to-[#005CB2]/60" />
        </div>
        
        <div className="eta-container relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {about.title || 'Eta Global Group'}
              </h1>
              
              <p className="text-xl text-white/90 leading-relaxed mb-10 max-w-xl">
                {about.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-5xl font-bold mb-1">{hero.stats?.[0]?.value || '10+'}</p>
                  <p className="text-white/70 text-sm font-medium">Yıl Deneyim</p>
                </div>
                <div className="text-center border-l border-r border-white/20">
                  <p className="text-5xl font-bold mb-1">{hero.stats?.[1]?.value || '500+'}</p>
                  <p className="text-white/70 text-sm font-medium">Mutlu Müşteri</p>
                </div>
                <div className="text-center">
                  <p className="text-5xl font-bold mb-1">{hero.stats?.[2]?.value || '4'}</p>
                  <p className="text-white/70 text-sm font-medium">Hizmet Alanı</p>
                </div>
              </div>
            </div>

            {/* Right - Feature Cards */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/25 transition-all duration-300 border border-white/10">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">Hedef Odaklı</h3>
                <p className="text-white/70 text-sm">Sonuç garantisi</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/25 transition-all duration-300 border border-white/10">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">Güvenilir</h3>
                <p className="text-white/70 text-sm">İş ortağınız</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/25 transition-all duration-300 border border-white/10">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Star className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">Profesyonel</h3>
                <p className="text-white/70 text-sm">Uzman kadro</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/25 transition-all duration-300 border border-white/10">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">İnsan Odaklı</h3>
                <p className="text-white/70 text-sm">Değer veriyoruz</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section - Clean & Bold */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="eta-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#005CB2] rounded-full mb-6">
                <Target className="w-10 h-10 text-white" />
              </div>
              <p className="text-[#005CB2] font-semibold tracking-wider mb-4">TEMEL PRENSİBİMİZ</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                "{about.mission}"
              </h2>
            </div>
            
            <p className="text-lg text-slate-600 text-center max-w-2xl mx-auto leading-relaxed">
              Kurumların ihtiyaçlarına uygun adayları titizlikle değerlendirerek 
              en doğru eşleşmeyi sağlamayı hedefliyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed About Section */}
      <section className="py-20 bg-slate-50" data-testid="detailed-about-section">
        <div className="eta-container">
          <div className="max-w-5xl mx-auto">
            {/* Main Introduction */}
            <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12 mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#005CB2] rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Hakkımızda
                </h2>
              </div>
              
              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p className="text-lg">
                  Sektördeki derin tecrübesi ve Türk bilgi birikimi ile yöneticilerimizin kurmuş olduğu <strong className="text-[#005CB2]">Eta Global Group</strong>, <strong className="text-slate-900">30 senelik sektör tecrübesiyle</strong> faaliyet göstermektedir.
                </p>
                
                <div className="bg-[#005CB2]/5 rounded-2xl p-6 border-l-4 border-[#005CB2]">
                  <p className="font-medium text-slate-800 mb-2">Hizmet Verdiğimiz İller:</p>
                  <p className="text-slate-600">
                    İstanbul merkezli olmak üzere; <strong>Çorlu, İzmir, Kocaeli, Bursa, Düzce, Konya, Kayseri, Adana, Ankara, Antalya, Eskişehir, Trabzon, Gaziantep, Diyarbakır, Şanlıurfa, Mardin, Malatya, Elazığ, Van ve Erzurum</strong> illerinde geniş bir hizmet ağına sahibiz.
                  </p>
                </div>
                
                <p>
                  Kargo dağıtım merkezleri, depolar, fabrikalar ve üretim tesislerinde; paketleme, elleçleme, ürün ayrıştırma ve dağıtım süreçlerinde uzman kadromuzla yanınızdayız. Mavi yaka (giriş seviyesi) ile beyaz yaka (daimi ve geçici) personel tedariki ve işe yerleştirme hizmetleri sunmaktayız.
                </p>
                
                <div className="flex items-center gap-3 bg-green-50 rounded-xl p-4 border border-green-200">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <p className="text-green-800 font-medium">
                    Şirketimiz, Türkiye İş Kurumu Özel İstihdam Bürosu izin belgesine sahiptir.
                  </p>
                </div>
              </div>
            </div>

            {/* Services Section */}
            <div className="mb-8">
              <div className="text-center mb-10">
                <p className="text-[#005CB2] font-semibold tracking-wider mb-3">HİZMET ALANLARIMIZ</p>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Çözümlerimiz
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Service 1 */}
                <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#005CB2] rounded-lg flex items-center justify-center text-white font-bold">
                      1
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Personel Tedariki ve Yönetimi</h4>
                  </div>
                  <p className="text-slate-600 mb-4 text-sm">
                    Personel istihdamı alanındaki uzmanlığımızla, müşterilerimizin üzerindeki iş yükünü hafifletmeyi amaçlıyoruz.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] mt-0.5 flex-shrink-0" />
                      <span><strong>Mavi Yaka:</strong> Kargo, lojistik ve depo personeli</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] mt-0.5 flex-shrink-0" />
                      <span><strong>Operasyonel:</strong> Yükleme-boşaltma, elleçleme, paketleme</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] mt-0.5 flex-shrink-0" />
                      <span><strong>Teknik:</strong> Reach Truck ve Forklift operatörü</span>
                    </li>
                  </ul>
                </div>

                {/* Service 2 */}
                <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#005CB2] rounded-lg flex items-center justify-center text-white font-bold">
                      2
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Seçme ve Yerleştirme Hizmeti</h4>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Kurumsal firmaların işe alım süreçlerini, insan kaynakları planlamalarına paralel olarak yönetiyoruz.
                  </p>
                  <div className="mt-4 bg-[#005CB2]/5 rounded-lg p-3">
                    <p className="text-sm text-[#005CB2] font-medium italic">
                      "Doğru işe, doğru iş gücünü yerleştirmek" anlayışıyla her personelin şirketin geleceğine bir adım ileriye taşımasını sağlıyoruz.
                    </p>
                  </div>
                </div>

                {/* Service 3 */}
                <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#005CB2] rounded-lg flex items-center justify-center text-white font-bold">
                      3
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Fabrika ve Üretim Tesisleri</h4>
                  </div>
                  <p className="text-slate-600 text-sm">
                    Üretim süreçlerinizin aksamaması için aşağıdaki hizmetleri günlük yevmiyeli veya daimi kadrolarla sağlıyoruz:
                  </p>
                  <ul className="mt-3 space-y-2">
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] flex-shrink-0" />
                      <span>Paketleme hizmetleri</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] flex-shrink-0" />
                      <span>Ürün ayrıştırma</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[#005CB2] flex-shrink-0" />
                      <span>Elleçleme ve genel üretim desteği</span>
                    </li>
                  </ul>
                </div>

                {/* Service 4 */}
                <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-shadow border border-slate-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#005CB2] rounded-lg flex items-center justify-center text-white font-bold">
                      4
                    </div>
                    <h4 className="text-lg font-bold text-slate-900">Tesis Yönetimi ve Saha Hizmetleri</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-slate-800 text-sm mb-1">Plaza ve İş Merkezleri:</p>
                      <p className="text-slate-600 text-sm">Danışma personeli, teknik personel ve temizlik personeli hizmetleri</p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm mb-1">Hastane Kurumları:</p>
                      <p className="text-slate-600 text-sm">Temizlik, danışma, hasta yönlendirme ve yardımcı hizmetler</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Modern Cards */}
      <section className="py-20 bg-slate-50" data-testid="values-section">
        <div className="eta-container">
          <div className="text-center mb-16">
            <p className="text-[#005CB2] font-semibold tracking-wider mb-4">DEĞERLERİMİZ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bizi Farklı Kılan Değerler
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#005CB2] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Güvenilirlik
                </h3>
                <p className="text-slate-600">Her zaman sözümüzün arkasındayız. Verdiğimiz taahhütleri eksiksiz yerine getiriyoruz.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#005CB2] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Profesyonellik
                </h3>
                <p className="text-slate-600">Alanında uzman kadromuzla en yüksek standartlarda hizmet sunuyoruz.</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#005CB2] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Müşteri Odaklılık
                </h3>
                <p className="text-slate-600">Sizin başarınız bizim başarımızdır. Her adımda yanınızdayız.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Us Section - Two Column with Images */}
      <section className="py-20 bg-white" data-testid="why-us-section">
        <div className="eta-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div>
              <p className="text-[#005CB2] font-semibold tracking-wider mb-4">NEDEN BİZ?</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Neden Eta Global<br />Group?
              </h2>
              
              <div className="space-y-3 mb-10">
                {(about.whyUs || [
                  'Geniş aday havuzu ve hızlı personel tedariki',
                  'Sektörel deneyim ve uzmanlık',
                  'Profesyonel değerlendirme süreçleri',
                  'Müşteri odaklı çözüm yaklaşımı',
                  '7/24 destek ve iletişim',
                  'Esnek ve ekonomik fiyatlandırma'
                ]).map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                    <div className="w-8 h-8 bg-[#005CB2] rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
              
              <Link to="/iletisim">
                <Button 
                  data-testid="contact-cta-btn"
                  size="lg"
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  Bizimle İletişime Geçin
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
            
            {/* Right - Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={aboutHero.whyus_image1 || "https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/8xuwsih2_ChatGPT%20Image%2017%20Mar%202026%2015_00_32.png"} 
                    alt="İnsan Kaynakları"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={aboutHero.whyus_image2 || "https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/vi7ky692_Gemini_Generated_Image_9hbmth9hbmth9hbm.png"} 
                    alt="Dijital Baskı"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={aboutHero.whyus_image3 || "https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/grf4qrup_Gemini_Generated_Image_ierh0nierh0nierh.png"} 
                    alt="Araç Kiralama"
                    className="w-full h-full object-cover object-right hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={aboutHero.whyus_image4 || "https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/gd9937yd_Gemini_Generated_Image_1jblzf1jblzf1jbl.png"} 
                    alt="Eğitim"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#005CB2]" data-testid="cta-section">
        <div className="eta-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            İş Birliğine Hazır mısınız?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Profesyonel çözümlerimiz hakkında bilgi almak ve teklif almak için hemen iletişime geçin.
          </p>
          <Link to="/iletisim">
            <Button 
              size="lg"
              className="bg-white text-[#005CB2] hover:bg-slate-100 gap-2 font-semibold"
            >
              <Clock className="w-5 h-5" />
              Hemen Başlayın
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
